import React, { useEffect, useState } from "react";
import { Alert, Card, Col, InputNumber, Row, Statistic, Typography, message, theme } from "antd";
import { useTranslation } from "react-i18next";
import { formatCents } from "../shared/money";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";

const EuerTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getEuer(year)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [year]);

  const strong = r => ["total_revenue", "total_expense", "result"].includes(r.kind);
  return (
    <div>
      <PageToolbar
        filters={
          <InputNumber
            addonBefore={t("Year")}
            value={year}
            onChange={v => v && setYear(v)}
            min={2000}
            max={2100}
          />
        }
      />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}>
          <Card size="small" className="acct-kpi">
            <Statistic title={t("Operating income")} value={formatCents(data?.betriebseinnahmen)} />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Operating expenses")}
              value={formatCents(data?.betriebsausgaben)}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Profit")}
              value={formatCents(data?.gewinn)}
              valueStyle={{ color: (data?.gewinn || 0) < 0 ? token.colorError : undefined }}
            />
          </Card>
        </Col>
      </Row>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "The line numbers follow the Anlage EÜR 2025 as far as known; the form changes every year. Verify them against the current official form before filing."
        )}
      />
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="line"
        dataSource={data?.lines || []}
        columns={[
          {
            title: t("Line"),
            dataIndex: "line",
            width: 70,
            render: v => <span className="acct-num">{v}</span>
          },
          {
            title: t("Description"),
            dataIndex: "label",
            render: (v, r) => <span style={{ fontWeight: strong(r) ? 600 : 400 }}>{v}</span>
          },
          {
            title: t("Accounts"),
            dataIndex: "accounts",
            render: a => <span className="acct-wrap">{(a || []).join(", ")}</span>,
            width: 220
          },
          {
            title: t("Amount"),
            dataIndex: "amount",
            align: "end",
            render: (v, r) => <Amount cents={v} strong={strong(r)} />,
            width: 160
          }
        ]}
      />
      {data?.avEuer ? (
        <div style={{ marginTop: 16 }}>
          <Typography.Title level={5}>{t("Anlage AVEÜR (asset schedule)")}</Typography.Title>
          <ResponsiveTable
            mobileLayout="table"
            className="acct-grid"
            size="small"
            pagination={false}
            rowKey="account"
            dataSource={data.avEuer.rows}
            columns={[
              { title: t("Account"), render: (_, r) => `${r.account} ${r.name}` },
              {
                title: t("Opening book value"),
                dataIndex: "openingBookValue",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("Additions"),
                dataIndex: "additions",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("Disposals"),
                dataIndex: "disposals",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("AfA"),
                dataIndex: "depreciation",
                align: "end",
                render: v => <Amount cents={v} />
              },
              {
                title: t("Closing book value"),
                dataIndex: "closingBookValue",
                align: "end",
                render: v => <Amount cents={v} />
              }
            ]}
          />
        </div>
      ) : null}
    </div>
  );
};

export default EuerTab;
